/**
 * ActiveAlertsTabContent Component
 *
 * Content for the Active Alerts tab in the dashboard.
 * Displays filtered alerts with empty state handling.
 */

import { BlockStack, Text } from '@shopify/polaris';
import { AlertCircleIcon } from '@shopify/polaris-icons';
import { TabEmptyState } from './TabEmptyState';
import { AlertsTable } from './AlertsTable';
import type { EnhancedAlert } from '../../types/dashboard';
import type { AppliedFilter } from '../../types/search';

/**
 * Props for ActiveAlertsTabContent component
 */
export interface ActiveAlertsTabContentProps {
  /** Filtered alerts to display */
  filteredAlerts: EnhancedAlert[];
  /** Total count of active alerts (before filtering) */
  activeAlertsCount: number;
  /** Current search query */
  searchQuery: string;
  /** Applied alert filters */
  appliedAlertFilters: AppliedFilter[];
  /** Set of alert IDs being dismissed */
  dismissingAlerts: Set<number>;
  /** Set of alert IDs being resolved */
  resolvingAlerts: Set<number>;
  /** Set of alert IDs being notified */
  notifyingAlerts: Set<number>;
  /** Handler to open resolve confirmation modal */
  onResolve: (alert: EnhancedAlert) => void;
  /** Handler to open dismiss confirmation modal */
  onDismiss: (alert: EnhancedAlert) => void;
  /** Handler to open notify confirmation modal */
  onNotify: (alert: EnhancedAlert) => void;
  /** Handler to open alert detail modal */
  onRowClick: (alert: EnhancedAlert) => void;
  /** Whether the shop has test data */
  hasTestData?: boolean;
  /** Whether notification channels are configured */
  hasNotificationChannels: boolean;
  /** Handler to clear all alert-related filters */
  onClearAllFilters: () => void;
  /** Handler to populate test data */
  onPopulateTestData: () => void;
  /** Whether test data is being generated */
  isGeneratingTestData: boolean;
}

/**
 * Tab content component for the Active Alerts tab
 */
export function ActiveAlertsTabContent({
  filteredAlerts,
  activeAlertsCount,
  searchQuery,
  appliedAlertFilters,
  dismissingAlerts,
  resolvingAlerts,
  notifyingAlerts,
  onResolve,
  onDismiss,
  onNotify,
  onRowClick,
  hasTestData,
  hasNotificationChannels,
  onClearAllFilters,
  onPopulateTestData,
  isGeneratingTestData,
}: ActiveAlertsTabContentProps) {
  const hasFilters = appliedAlertFilters.length > 0 || searchQuery;

  if (filteredAlerts.length === 0) {
    return (
      <div className="empty-state-section">
        <TabEmptyState
          icon={hasFilters ? AlertCircleIcon : undefined}
          illustration={hasFilters ? undefined : 'no-active-alerts'}
          title={hasFilters ? 'No alerts match your filters' : 'No active alerts'}
          description={
            hasFilters
              ? "Try adjusting your search or filters to find what you're looking for"
              : 'Get started by populating your store with test data to see how alerts work'
          }
          action={
            hasFilters
              ? { content: 'Clear all filters', onClick: onClearAllFilters }
              : {
                  content: 'Populate with test data',
                  onClick: onPopulateTestData,
                  variant: 'primary',
                  loading: isGeneratingTestData,
                }
          }
        />
      </div>
    );
  }

  return (
    <BlockStack gap="300">
      <AlertsTable
        alerts={filteredAlerts}
        dismissingAlerts={dismissingAlerts}
        resolvingAlerts={resolvingAlerts}
        notifyingAlerts={notifyingAlerts}
        onResolve={onResolve}
        onDismiss={onDismiss}
        onNotify={onNotify}
        onRowClick={onRowClick}
        hasTestData={hasTestData}
        hasNotificationChannels={hasNotificationChannels}
      />
      <Text as="p" variant="bodySm" tone="subdued">
        Showing {filteredAlerts.length} of {activeAlertsCount} active alerts
      </Text>
    </BlockStack>
  );
}
